import { useState, useEffect, useRef } from "react";
import { Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import Images from "../../../utils/Images";
import { useNavigate, useLocation } from "react-router-dom";
import authService from "../../../api/AuthService";
import { toast } from "react-toastify";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef([]);
  
  const { contact, consumer_slug } = location.state || {};

  // Check if required state is available
  useEffect(() => {
    // This effect can be used for any initialization logic
    // The routing system will handle redirects if needed
  }, []);

  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const isOtpComplete = otp.every(digit => digit !== "");
  const isFormValid = newPassword.length >= 6 && passwordsMatch && isOtpComplete;

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split("").concat(["", "", "", ""]).slice(0, 4);
      setOtp(newOtp);
      
      // Focus the next empty input or the last one
      const nextEmptyIndex = newOtp.findIndex((digit, i) => !digit && i < 4);
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[3]?.focus();
      }
    }
  };

  const handleResetPassword = async () => {
    if (isFormValid) {
      setIsSubmitting(true);
      try {
        const payload = {
          password: newPassword,
          otp: parseInt(otp.join("")),
          slug: consumer_slug,
        };
        
        const response = await authService.resetPassword(payload);
        if (response.status) {
          toast.success(response.message || "Password reset successfully!");
          navigate("/signin");
        } else {
          toast.error(response.message || "Password reset failed!");
        }
      } catch (error) {
        toast.error("An error occurred during password reset");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="relative min-h-screen">
      <img 
        src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=1600&auto=format&fit=crop" 
        alt="bg" 
        className="object-cover absolute inset-0 w-full h-full" 
      />
      <div className="absolute inset-0 bg-black/60" />
      
      <div className="flex relative z-10 justify-center items-start pt-10">
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
      </div>
      
      <div className="flex relative z-10 justify-center items-center p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="p-6 mt-4 w-full max-w-md rounded-3xl shadow-2xl bg-white/95"
        >
          <h2 className="text-lg font-semibold text-center">Create New Password</h2>
          <p className="mt-1 text-xs text-center text-gray-500">
            Enter the OTP code and your new password to complete the reset
          </p>

          <div className="mt-5 space-y-4">
            {/* OTP Input */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Enter OTP Code
              </label>
              <div className="flex gap-3 justify-center">
                {otp.map((digit, index) => (
                  <motion.div 
                    key={index} 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.1, duration: 0.4 }}
                    className="relative group"
                  >
                    <div className="relative rounded-full p-[2px] bg-gradient-to-br from-emerald-400/40 via-transparent to-emerald-400/40 transition-all duration-300 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
                      <motion.input
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={index === 0 ? handleOtpPaste : undefined}
                        whileFocus={{ scale: 1.05 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className="w-14 h-14 text-lg font-bold text-center rounded-full border-0 transition-all duration-300 outline-none bg-white/95 focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none focus:bg-white focus:text-emerald-700"
                        maxLength="1"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
              <p className="mt-2 text-xs text-center text-gray-500">
                Enter the 4-digit code sent to your {contact}
              </p>
            </div>
            {/* New Password */}
            <div className="relative group">
              <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-transparent to-emerald-400/30 transition-all duration-300 group-hover:from-emerald-400/50 group-hover:to-emerald-400/50 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
                <div className="flex items-center gap-3 rounded-[0.95rem] bg-white/95 px-4 py-3 ring-1 ring-gray-200/50 shadow-sm backdrop-blur-md transition-all duration-300 group-hover:ring-emerald-300/60 group-hover:shadow-md group-focus-within:ring-emerald-400/80 group-focus-within:shadow-lg group-focus-within:bg-white">
                  <Lock size={18} className="text-emerald-600 transition-colors duration-300 group-focus-within:text-emerald-700" />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    className="w-full text-sm font-medium bg-transparent border-0 transition-colors duration-300 outline-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(v => !v)}
                    className="text-gray-400 transition-colors duration-300 hover:text-emerald-600 focus:outline-none focus:text-emerald-700"
                    aria-label="Toggle password visibility"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="relative group">
              <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-transparent to-emerald-400/30 transition-all duration-300 group-hover:from-emerald-400/50 group-hover:to-emerald-400/50 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
                <div className="flex items-center gap-3 rounded-[0.95rem] bg-white/95 px-4 py-3 ring-1 ring-gray-200/50 shadow-sm backdrop-blur-md transition-all duration-300 group-hover:ring-emerald-300/60 group-hover:shadow-md group-focus-within:ring-emerald-400/80 group-focus-within:shadow-lg group-focus-within:bg-white">
                  <Lock size={18} className="text-emerald-600 transition-colors duration-300 group-focus-within:text-emerald-700" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full text-sm font-medium bg-transparent border-0 transition-colors duration-300 outline-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="text-gray-400 transition-colors duration-300 hover:text-emerald-600 focus:outline-none focus:text-emerald-700"
                    aria-label="Toggle password visibility"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Match Indicator */}
            {confirmPassword && (
              <div className={`flex items-center gap-2 text-xs transition-colors duration-300 ${
                passwordsMatch ? "text-emerald-600" : "text-red-500"
              }`}>
                <CheckCircle2 size={14} className={passwordsMatch ? "text-emerald-600" : "text-red-500"} />
                {passwordsMatch ? "Passwords match" : "Passwords don't match"}
              </div>
            )}

            {/* Password Requirements */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              className="space-y-1 text-xs text-gray-500"
            >
              <p>Password requirements:</p>
              <ul className="ml-2 space-y-1">
                <motion.li 
                  animate={{ 
                    color: newPassword.length >= 6 ? "#10b981" : "#9ca3af",
                    scale: newPassword.length >= 6 ? [1, 1.02, 1] : 1
                  }}
                  transition={{ duration: 0.3 }}
                  className="transition-colors duration-300"
                >
                  • At least 6 characters
                </motion.li>
              </ul>
            </motion.div>

            {/* Reset Password Button */}
            <motion.button
              onClick={handleResetPassword}
              disabled={!isFormValid || isSubmitting}
              whileHover={{ scale: isFormValid && !isSubmitting ? 1.02 : 1 }}
              whileTap={{ scale: isFormValid && !isSubmitting ? 0.98 : 1 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.4 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg transition-all duration-300 group hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <motion.span 
                className="flex relative z-10 gap-2 justify-center items-center"
                animate={isFormValid && !isSubmitting ? { scale: [1, 1.02, 1] } : { scale: 1 }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-4 h-4 rounded-full border-2 border-white border-t-transparent"
                    />
                    Resetting Password...
                  </>
                ) : (
                  "Reset Password"
                )}
              </motion.span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full"></div>
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
